import React, { useState, useEffect } from 'react';
import axios from './lib/axiosClient';

function Waitlist() {
  var [entries, setEntries] = useState([]);
  var [customers, setCustomers] = useState([]);
  var [services, setServices] = useState([]);
  var [showAdd, setShowAdd] = useState(false);
  var [loading, setLoading] = useState(true);
  var [form, setForm] = useState({ contact_id: '', service_id: '', preferred_date: '', preferred_time: '', notes: '' });
  var [search, setSearch] = useState('');

  useEffect(function() { loadAll(); }, []);

  var loadAll = async function() {
    try {
      var [wRes, cRes, sRes] = await Promise.all([
        axios.get('/api/waitlist'),
        axios.get('/api/customers'),
        axios.get('/api/services?active_only=true'),
      ]);
      setEntries(wRes.data.waitlist || []);
      setCustomers(cRes.data.customers || []);
      setServices(sRes.data.services || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  var handleAdd = async function() {
    if (!form.contact_id) { alert('Select a customer'); return; }
    try {
      await axios.post('/api/waitlist', form);
      setShowAdd(false);
      setForm({ contact_id: '', service_id: '', preferred_date: '', preferred_time: '', notes: '' });
      setSearch('');
      loadAll();
    } catch (e) { alert('Error adding to waitlist'); }
  };

  var handleRemove = async function(id) {
    await axios.delete('/api/waitlist/' + id);
    loadAll();
  };

  var handleBooked = async function(id) {
    await axios.post('/api/waitlist/' + id + '/booked');
    loadAll();
  };

  var filtered = customers.filter(function(c) {
    var q = search.toLowerCase();
    return (c.first_name + ' ' + c.last_name + ' ' + (c.phone || '')).toLowerCase().includes(q);
  }).slice(0, 8);

  var statusColors = { waiting: '#f59e0b', notified: '#3b82f6', booked: '#10b981' };
  var statusLabels = { waiting: '⏳ Waiting', notified: '📱 Notified', booked: '✅ Booked' };

  if (loading) return <div style={S.loading}>Loading waitlist...</div>;

  return (
    <div style={{ maxWidth: '100%' }}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>📋 Waitlist</h1>
          <p style={S.subtitle}>When a booking gets cancelled, we auto-text the next person on the list</p>
        </div>
        <button onClick={function() { setShowAdd(true); }} style={S.addBtn}>+ Add to Waitlist</button>
      </div>

      {/* How It Works */}
      <div style={{ background: '#0f172a', borderRadius: 12, padding: 18, marginBottom: 24, border: '1px solid #1e3a5f' }}>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
          {[
            { icon: '📋', text: 'Add customer to waitlist' },
            { icon: '❌', text: 'A booking gets cancelled' },
            { icon: '📱', text: 'Customer gets auto-texted' },
            { icon: '✅', text: 'They book the open slot' },
          ].map(function(step, i) {
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 20 }}>{step.icon}</span>
                <span style={{ color: '#94a3b8', fontSize: 13 }}>{step.text}</span>
                {i < 3 && <span style={{ color: '#334155', fontSize: 16 }}>→</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Waitlist Entries */}
      {entries.length > 0 ? (
        <div style={{ display: 'grid', gap: 8 }}>
          {entries.map(function(e) {
            return (
              <div key={e.id} style={S.row}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ color: '#fff', fontWeight: 600 }}>{e.contacts?.first_name} {e.contacts?.last_name}</span>
                    <span style={{ color: statusColors[e.status] || '#888', fontSize: 12, fontWeight: 600 }}>{statusLabels[e.status] || e.status}</span>
                  </div>
                  <div style={{ color: '#888', fontSize: 13, marginTop: 4 }}>
                    {e.services?.name || 'Any service'}
                    {e.preferred_date && <span> · {e.preferred_date}</span>}
                    {e.preferred_time && <span> at {e.preferred_time}</span>}
                    {e.notes && <span> · {e.notes}</span>}
                  </div>
                  <div style={{ color: '#555', fontSize: 11, marginTop: 4 }}>
                    Added {new Date(e.created_at).toLocaleDateString()}
                    {e.notified_at && <span> · Notified {new Date(e.notified_at).toLocaleString()}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {e.status === 'notified' && (
                    <button onClick={function() { handleBooked(e.id); }} style={S.bookedBtn}>✅ Booked</button>
                  )}
                  <button onClick={function() { handleRemove(e.id); }} style={S.removeBtn}>✕</button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: 60, color: '#888' }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>📋</div>
          <div>No one on the waitlist yet</div>
          <div style={{ fontSize: 13, marginTop: 8, color: '#666' }}>Add customers who want a slot but none are available</div>
        </div>
      )}

      {/* Add to Waitlist Modal */}
      {showAdd && (
        <div style={S.overlay} onClick={function() { setShowAdd(false); }}>
          <div style={S.modal} onClick={function(e) { e.stopPropagation(); }}>
            <h2 style={{ color: '#fff', margin: '0 0 20px' }}>Add to Waitlist</h2>
            <div style={S.field}>
              <label style={S.label}>Customer</label>
              <input style={S.input} value={search} onChange={function(e) { setSearch(e.target.value); setForm({ ...form, contact_id: '' }); }} placeholder="Search by name or phone..." />
              {search && !form.contact_id && (
                <div style={{ maxHeight: 160, overflowY: 'auto', marginTop: 4 }}>
                  {filtered.map(function(c) {
                    return (
                      <div key={c.id} onClick={function() { setForm({ ...form, contact_id: c.id }); setSearch(c.first_name + ' ' + c.last_name); }}
                        style={{ padding: '8px 12px', cursor: 'pointer', color: '#fff', fontSize: 14, borderBottom: '1px solid #1e1e35' }}>
                        {c.first_name} {c.last_name} <span style={{ color: '#888' }}>{c.phone}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={S.field}>
              <label style={S.label}>Service (optional)</label>
              <select style={S.input} value={form.service_id} onChange={function(e) { setForm({ ...form, service_id: e.target.value }); }}>
                <option value="">Any service</option>
                {services.map(function(s) { return <option key={s.id} value={s.id}>{s.name}</option>; })}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={S.field}>
                <label style={S.label}>Preferred Date</label>
                <input style={S.input} type="date" value={form.preferred_date} onChange={function(e) { setForm({ ...form, preferred_date: e.target.value }); }} />
              </div>
              <div style={S.field}>
                <label style={S.label}>Preferred Time</label>
                <input style={S.input} type="time" value={form.preferred_time} onChange={function(e) { setForm({ ...form, preferred_time: e.target.value }); }} />
              </div>
            </div>
            <div style={S.field}>
              <label style={S.label}>Notes (optional)</label>
              <input style={S.input} value={form.notes} onChange={function(e) { setForm({ ...form, notes: e.target.value }); }} placeholder="e.g. Wants morning slot with Sarah" />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={function() { setShowAdd(false); }} style={S.cancelBtn}>Cancel</button>
              <button onClick={handleAdd} style={S.saveBtn}>Add to Waitlist</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

var S = {
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: '#888', fontSize: 18 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 26, fontWeight: 800, color: '#fff', margin: '0 0 6px' },
  subtitle: { fontSize: 14, color: '#888', margin: 0 },
  addBtn: { padding: '12px 24px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: 12, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  row: { display: 'flex', alignItems: 'center', gap: 16, background: '#12121f', borderRadius: 12, padding: '14px 18px', border: '1px solid #1e1e35' },
  bookedBtn: { padding: '6px 14px', background: '#10b981', border: 'none', borderRadius: 6, color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' },
  removeBtn: { padding: '6px 10px', background: '#334155', border: 'none', borderRadius: 6, color: '#888', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: 20 },
  modal: { background: '#12121f', borderRadius: 16, padding: 28, maxWidth: 480, width: '100%', border: '1px solid #1e1e35', maxHeight: '90vh', overflowY: 'auto' },
  field: { marginBottom: 14 },
  label: { display: 'block', color: '#aaa', fontSize: 12, fontWeight: 600, marginBottom: 6 },
  input: { width: '100%', padding: '10px 14px', background: '#252840', border: '1px solid #2a2a4a', borderRadius: 8, color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  cancelBtn: { flex: 1, padding: 12, background: '#334155', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, cursor: 'pointer' },
  saveBtn: { flex: 1, padding: 12, background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, cursor: 'pointer' },
};

export default Waitlist;
